/**
 * LayeredCard - Card with stacked layers creating 3D depth effect
 *
 * Features:
 * - Multiple visible background layers
 * - Hover animation lifting the card
 * - Creates visual depth without true 3D transforms
 */

"use client";

import { ReactNode } from "react";

interface LayeredCardProps {
  children: ReactNode;
  className?: string;
  /** Number of background layers (1-3) */
  layers?: 1 | 2 | 3;
}

export default function LayeredCard({
  children,
  className = "",
  layers = 2,
}: LayeredCardProps) {
  return (
    <div className={`relative group ${className}`}>
      {/* Layer 3 (furthest back) */}
      {layers >= 3 && (
        <div
          className="
            absolute inset-0 rounded-2xl
            bg-purple-500/10 border border-purple-500/20
            translate-x-3 translate-y-3
            transition-transform duration-300
            group-hover:translate-x-4 group-hover:translate-y-4
          "
        />
      )}

      {/* Layer 2 (middle) */}
      {layers >= 2 && (
        <div
          className="
            absolute inset-0 rounded-2xl
            bg-pink-500/15 border border-pink-500/20
            translate-x-1.5 translate-y-1.5
            transition-transform duration-300
            group-hover:translate-x-2 group-hover:translate-y-2
          "
        />
      )}

      {/* Layer 1 (closest back layer) */}
      <div
        className="
          absolute inset-0 rounded-2xl
          bg-pink-500/20 border border-pink-500/30
          translate-x-0.5 translate-y-0.5
          transition-transform duration-300
          group-hover:translate-x-1 group-hover:translate-y-1
        "
      />

      {/* Main card (front) */}
      <div
        className="
          relative rounded-2xl
          bg-gray-900/95 border border-gray-700/50
          backdrop-blur-xl
          transition-transform duration-300
          group-hover:-translate-x-0.5 group-hover:-translate-y-0.5
          h-full
        "
      >
        {children}
      </div>
    </div>
  );
}
